import React, { useState } from "react";
import { Check } from "lucide-react";
import { PREVIEWS, PACKAGES, PACKAGE_FEATURES, CONTACT, LEGAL, formatTL } from "../../mock";
import Reveal from "./Reveal";
import { Logo } from "./Navbar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";

/* ---------------- UYGULAMA ÖNİZLEMESİ ---------------- */
export const AppPreview = () => (
  <section className="bg-white/90 backdrop-blur-md">
    <div className="max-w-6xl mx-auto px-6 py-16">
      <Reveal>
        <div className="text-center">
          <div className="text-[11px] tracking-[0.3em] font-semibold text-neutral-400">
            NASIL GÖRÜNÜYOR
          </div>
          <h2 className="mt-3 text-3xl font-bold text-black">
            Uygulama önizlemesi
          </h2>
        </div>
      </Reveal>

      <div className="mt-12 grid sm:grid-cols-3 gap-5 max-w-4xl mx-auto">
        {PREVIEWS.map((p, i) => (
          <Reveal key={p.title} delay={i * 80}>
            <div className="bg-[#f5f5f5] rounded-2xl px-6 py-8 text-center h-full hover:bg-[#efefef] hover:-translate-y-1 transition-[background-color,transform] duration-300">
              <div className="w-14 h-14 bg-white rounded-full shadow-sm mx-auto flex items-center justify-center text-3xl">
                {p.emoji}
              </div>
              <div className="mt-4 text-base font-bold text-black">
                {p.title}
              </div>
              <p className="mt-2 text-[11px] leading-4 text-neutral-500">
                {p.desc}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- PAKETLER ---------------- */
const PackageCard = ({ p, onSelect }) => {
  const popular = p.popular;
  return (
    <div
      data-testid={`package-card-${p.id}`}
      className={`relative rounded-2xl p-6 pt-7 h-full flex flex-col text-center transition-transform duration-300 hover:-translate-y-1 ${
        popular
          ? "bg-black text-white shadow-[0_25px_50px_-12px_rgba(0,0,0,0.4)]"
          : "bg-white border border-neutral-200 shadow-sm"
      }`}
    >
      {popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-black text-[10px] font-bold tracking-wider px-3 py-1 rounded-full">
          EN POPÜLER
        </div>
      )}
      <div className={`text-xs font-semibold tracking-[0.2em] ${popular ? "text-amber-300" : "text-neutral-400"}`}>
        {p.label.toUpperCase()}
      </div>
      <div className="mt-3">
        <span data-testid={`package-price-${p.id}`} className="text-3xl font-extrabold tracking-tight">
          ₺{formatTL(p.price)}
        </span>
      </div>
      <div className={`mt-1 text-[11px] ${popular ? "text-neutral-400" : "text-neutral-500"}`}>
        {p.months > 1 ? `Aylık ₺${formatTL(p.price / p.months)} · ${p.months} ay` : "Aylık ödeme"}
      </div>
      {p.months > 1 && (
        <div data-testid={`package-saving-${p.id}`} className="mt-2 mx-auto text-[10px] font-semibold text-green-600 bg-green-50 rounded-full px-2.5 py-0.5">
          ₺{formatTL(PACKAGES[0].price * p.months - p.price)} tasarruf
        </div>
      )}
      <button
        data-testid={`package-select-${p.id}`}
        onClick={() => onSelect(p)}
        className={`mt-6 mx-auto block text-xs font-semibold px-8 py-2.5 rounded-lg transition-colors ${
          popular ? "bg-white text-black hover:bg-amber-100" : "bg-neutral-200 text-black hover:bg-neutral-300"
        }`}
      >
        Hemen Başla
      </button>
    </div>
  );
};

export const Packages = ({ onSelect }) => (
  <section id="paketler" data-testid="packages-section" className="bg-[#fafafa]/90 backdrop-blur-md">
    <div className="max-w-6xl mx-auto px-6 py-20">
      <Reveal>
        <div>
          <div className="text-center">
            <div className="text-[11px] tracking-[0.3em] font-semibold text-neutral-400">
              PAKETLER
            </div>
            <h2 className="mt-3 text-3xl font-bold text-black">Tek paket, size uygun süre</h2>
            <p className="mt-2 text-sm text-neutral-500">
              Bireysel kullanıcılar ve işletmeler için aynı paket
            </p>
          </div>
        </div>
      </Reveal>

      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
        {PACKAGES.map((p, i) => (
          <Reveal key={p.id} delay={i * 80}>
            <PackageCard p={p} onSelect={onSelect} />
          </Reveal>
        ))}
      </div>

      <Reveal>
        <ul data-testid="package-features" className="mt-10 flex flex-wrap justify-center gap-x-6 gap-y-3">
          {PACKAGE_FEATURES.map((f) => (
            <li key={f} className="flex items-center gap-2 text-xs text-neutral-600">
              <Check className="w-3.5 h-3.5 text-green-600" strokeWidth={2.5} />
              {f}
            </li>
          ))}
        </ul>
      </Reveal>
    </div>
  </section>
);

/* ---------------- FOOTER ---------------- */
export const Footer = () => {
  const [legal, setLegal] = useState(null);

  return (
    <footer className="bg-white border-t border-neutral-100">
      <div className="max-w-6xl mx-auto px-6 py-14 flex flex-col items-center text-center">
        <Logo size="lg" />
        <div className="mt-4 text-xs text-neutral-500">
          Balıkesir - BETA İş Platformu © 2026
        </div>
        <a
          href={CONTACT.whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 text-xs hover:underline"
        >
          <span className="font-semibold text-green-600">WhatsApp:</span>{" "}
          <span className="text-neutral-600">{CONTACT.whatsapp}</span>
        </a>
        <div className="mt-3 flex items-center gap-2 text-[11px] text-neutral-400">
          <button
            onClick={() => setLegal(LEGAL.sozlesme)}
            className="underline underline-offset-2 hover:text-neutral-600 transition-colors"
          >
            Mesafeli Satış Sözleşmesi
          </button>
          <span>·</span>
          <button
            onClick={() => setLegal(LEGAL.kvkk)}
            className="underline underline-offset-2 hover:text-neutral-600 transition-colors"
          >
            Gizlilik Politikası (KVKK)
          </button>
        </div>
      </div>

      <Dialog open={!!legal} onOpenChange={() => setLegal(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{legal?.title}</DialogTitle>
            <DialogDescription className="sr-only">
              {legal?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-neutral-600 leading-6 whitespace-pre-line max-h-[60vh] overflow-y-auto pr-2">
            {legal?.body}
          </div>
        </DialogContent>
      </Dialog>
    </footer>
  );
};
